import { useState, useCallback } from 'react';
import type { ProductFormData } from '@/types';
import MediaUploader from './MediaUploader';
import DescriptionEditor from './DescriptionEditor';
import SpecsBuilder from './SpecsBuilder';
import VariationManager from './VariationManager';
import PricingCalculator from './PricingCalculator';

interface ProductFormProps {
  data: ProductFormData;
  errors: Record<string, string>;
  onChange: <K extends keyof ProductFormData>(key: K, value: ProductFormData[K]) => void;
  onSubmit: () => Promise<string | null>;
  loading: boolean;
  mode: 'create' | 'edit';
}

const tabs = [
  { id: 'general', label: 'General' },
  { id: 'media', label: 'Media' },
  { id: 'description', label: 'Description' },
  { id: 'specs', label: 'Specifications' },
  { id: 'variations', label: 'Variations' },
  { id: 'seo', label: 'SEO' },
  { id: 'pricing', label: 'Pricing' },
];

export default function ProductForm({ data, errors, onChange, onSubmit, loading, mode }: ProductFormProps) {
  const [activeTab, setActiveTab] = useState('general');

  const handleDescriptionChange = useCallback(
    (html: string) => {
      onChange('description', html);
    },
    [onChange],
  );

  const handleSpecsChange = useCallback(
    (specs: Record<string, string>) => {
      onChange('specs', specs);
    },
    [onChange],
  );

  const handleVariationsChange = useCallback(
    (variations: ProductFormData['variations']) => {
      onChange('variations', variations);
    },
    [onChange],
  );

  const handleImagesChange = useCallback(
    (images: ProductFormData['images']) => {
      onChange('images', images);
    },
    [onChange],
  );

  const handleVideoChange = useCallback(
    (video: ProductFormData['video']) => {
      onChange('video', video);
    },
    [onChange],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit();
    },
    [onSubmit],
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="card">
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-6">
          {activeTab === 'general' && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label-field">Product Name *</label>
                <input
                  type="text"
                  value={data.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  className={`input-field mt-1 ${errors.name ? 'border-destructive' : ''}`}
                  placeholder="Enter product name"
                />
                {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
              </div>

              <div>
                <label className="label-field">Slug</label>
                <input
                  type="text"
                  value={data.slug}
                  onChange={(e) => onChange('slug', e.target.value)}
                  className="input-field mt-1"
                  placeholder="auto-generated"
                />
              </div>

              <div>
                <label className="label-field">Brand</label>
                <input
                  type="text"
                  value={data.brand}
                  onChange={(e) => onChange('brand', e.target.value)}
                  className="input-field mt-1"
                  placeholder="e.g., Apple, Samsung"
                />
              </div>

              <div>
                <label className="label-field">Category *</label>
                <select
                  value={data.category}
                  onChange={(e) => onChange('category', e.target.value)}
                  className={`input-field mt-1 ${errors.category ? 'border-destructive' : ''}`}
                >
                  <option value="">Select category</option>
                  <option value="smartphones">Smartphones</option>
                  <option value="laptops">Laptops</option>
                  <option value="tablets">Tablets</option>
                  <option value="audio">Audio</option>
                  <option value="wearables">Wearables</option>
                  <option value="accessories">Accessories</option>
                  <option value="gaming">Gaming</option>
                  <option value="smart-home">Smart Home</option>
                </select>
                {errors.category && <p className="mt-1 text-xs text-destructive">{errors.category}</p>}
              </div>

              <div>
                <label className="label-field">SKU *</label>
                <input
                  type="text"
                  value={data.sku}
                  onChange={(e) => onChange('sku', e.target.value)}
                  className={`input-field mt-1 ${errors.sku ? 'border-destructive' : ''}`}
                  placeholder="e.g., SGG-001"
                />
                {errors.sku && <p className="mt-1 text-xs text-destructive">{errors.sku}</p>}
              </div>

              <div>
                <label className="label-field">Price (SGD) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={data.price || ''}
                  onChange={(e) => onChange('price', parseFloat(e.target.value) || 0)}
                  className={`input-field mt-1 ${errors.price ? 'border-destructive' : ''}`}
                  placeholder="0.00"
                />
                {errors.price && <p className="mt-1 text-xs text-destructive">{errors.price}</p>}
              </div>

              <div>
                <label className="label-field">Compare at Price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={data.compareAtPrice || ''}
                  onChange={(e) => onChange('compareAtPrice', parseFloat(e.target.value) || undefined)}
                  className="input-field mt-1"
                  placeholder="Original price"
                />
              </div>

              <div>
                <label className="label-field">Stock *</label>
                <input
                  type="number"
                  min="0"
                  value={data.stock}
                  onChange={(e) => onChange('stock', parseInt(e.target.value) || 0)}
                  className={`input-field mt-1 ${errors.stock ? 'border-destructive' : ''}`}
                  placeholder="0"
                />
                {errors.stock && <p className="mt-1 text-xs text-destructive">{errors.stock}</p>}
              </div>

              <div>
                <label className="label-field">Status</label>
                <select
                  value={data.status}
                  onChange={(e) => onChange('status', e.target.value as ProductFormData['status'])}
                  className="input-field mt-1"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="label-field">Tags</label>
                <input
                  type="text"
                  value={data.tags.join(', ')}
                  onChange={(e) =>
                    onChange(
                      'tags',
                      e.target.value
                        .split(',')
                        .map((t) => t.trim())
                        .filter(Boolean),
                    )
                  }
                  className="input-field mt-1"
                  placeholder="Comma-separated tags"
                />
              </div>
            </div>
          )}

          {activeTab === 'media' && (
            <MediaUploader
              images={data.images}
              video={data.video}
              onImagesChange={handleImagesChange}
              onVideoChange={handleVideoChange}
            />
          )}

          {activeTab === 'description' && (
            <DescriptionEditor
              value={data.description}
              onChange={handleDescriptionChange}
            />
          )}

          {activeTab === 'specs' && (
            <SpecsBuilder
              specs={data.specs}
              onChange={handleSpecsChange}
            />
          )}

          {activeTab === 'variations' && (
            <VariationManager
              variations={data.variations}
              images={data.images}
              onChange={handleVariationsChange}
            />
          )}

          {activeTab === 'seo' && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label-field">SEO Title *</label>
                <input
                  type="text"
                  value={data.seoTitle}
                  onChange={(e) => onChange('seoTitle', e.target.value)}
                  className={`input-field mt-1 ${errors.seoTitle ? 'border-destructive' : ''}`}
                  placeholder="SEO optimized title"
                  maxLength={60}
                />
                <p className="mt-1 text-2xs text-muted-foreground">{data.seoTitle.length}/60</p>
                {errors.seoTitle && <p className="mt-1 text-xs text-destructive">{errors.seoTitle}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="label-field">SEO Description *</label>
                <textarea
                  value={data.seoDescription}
                  onChange={(e) => onChange('seoDescription', e.target.value)}
                  className={`textarea-field mt-1 ${errors.seoDescription ? 'border-destructive' : ''}`}
                  placeholder="Meta description for search engines"
                  maxLength={160}
                  rows={3}
                />
                <p className="mt-1 text-2xs text-muted-foreground">{data.seoDescription.length}/160</p>
                {errors.seoDescription && <p className="mt-1 text-xs text-destructive">{errors.seoDescription}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="label-field">SEO Keywords</label>
                <input
                  type="text"
                  value={data.seoKeywords.join(', ')}
                  onChange={(e) =>
                    onChange(
                      'seoKeywords',
                      e.target.value
                        .split(',')
                        .map((k) => k.trim())
                        .filter(Boolean),
                    )
                  }
                  className="input-field mt-1"
                  placeholder="Comma-separated keywords"
                />
              </div>
            </div>
          )}

          {activeTab === 'pricing' && (
            <PricingCalculator
              cost={data.cost}
              price={data.price}
              onCostChange={(cost) => onChange('cost', cost)}
              onPriceChange={(price) => onChange('price', price)}
            />
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <button type="button" className="btn-secondary">
          Save Draft
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? (
            <>
              <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Saving...
            </>
          ) : mode === 'create' ? (
            'Create Product'
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </form>
  );
}
